import React from 'react';
import { useTranslation, resolveResourceLang } from './i18n.js';

function LegalModal({ title, resourceName, onClose }) {
  const { lang, t } = useTranslation();
  const [html, setHtml] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    setError('');

    const resourceLang = resolveResourceLang(lang);

    fetch(`/resources/${resourceLang}/${resourceName}.html`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(t('contentErrorLoad'));
        }
        return response.text();
      })
      .then((text) => {
        setHtml(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || t('contentErrorLoad'));
        setLoading(false);
      });
  }, [lang, resourceName]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="legal-modal-overlay" onClick={onClose}>
      <div
        className="legal-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="legal-modal-close"
          aria-label={t('footerCloseLabel')}
          onClick={onClose}
        >
          &times;
        </button>
        {loading && (
          <div className="loading">
            <progress />
            <p>{t('contentLoading')}</p>
          </div>
        )}
        {error && (
          <div className="error">
            <strong>{t('contentErrorPrefix')}</strong> {error}
          </div>
        )}
        {!loading && !error && (
          <div className="legal-modal-content" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
    </div>
  );
}

export function Footer() {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = React.useState(null);
  const year = new Date().getFullYear();

  return (
    <footer className="site-footer">
      <div className="site-footer-content">
        <p className="site-footer-copyright">
          © {year} Éditions Prosveta S.A. — {t('footerRightsReserved')}
        </p>
        <nav className="site-footer-links">
          <button type="button" className="site-footer-link" onClick={() => setOpenModal('legal')}>
            {t('footerLegalNoticeLink')}
          </button>
          <button type="button" className="site-footer-link" onClick={() => setOpenModal('privacy')}>
            {t('footerPrivacyPolicyLink')}
          </button>
          <button
            type="button"
            className="site-footer-link"
            onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-preferences'))}
          >
            {t('footerCookieSettingsLink')}
          </button>
        </nav>
      </div>
      {openModal === 'legal' && (
        <LegalModal
          title={t('footerLegalNoticeLink')}
          resourceName="legal-notice"
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === 'privacy' && (
        <LegalModal
          title={t('footerPrivacyPolicyLink')}
          resourceName="privacy-policy"
          onClose={() => setOpenModal(null)}
        />
      )}
    </footer>
  );
}
